import format from "date-fns/format"
import parseISO from "date-fns/parseISO"
import PropTypes from "prop-types"
import React, { useMemo, useState } from "react"
import { useQuery } from "react-query"
import ReservationNightTable from "src/main/Manage/ReservationNightTable"

import { getNightCounts } from "src/api/Manage/Reservations"

import AvailabilityBarContainer from "./AvailabilityBarContainer"
import NightViewHeader from "./NightViewHeader"
import NotesBar from "./NotesBar"
import { useNightViewFilters } from "./useNightViewFilters"

const NightViewPage = ({
  night: initialNight,
  counts: initialCounts,
  marinaSlug,
  paths: initialPaths,
  reservationNightTableProps,
}) => {
  const filtersHook = useNightViewFilters()
  const [currentNight, setCurrentNight] = useState(initialNight)
  const date = parseISO(currentNight)

  const { data: counts, isLoading: countsLoading } = useQuery(
    ["nightCounts", marinaSlug, currentNight],
    () => getNightCounts(marinaSlug, currentNight),
    {
      initialData: currentNight === initialNight ? initialCounts : undefined,
      refetchOnWindowFocus: false,
    }
  )

  const paths = useMemo(() => {
    if (currentNight === initialNight) return initialPaths

    const currentUrl = new URL(window.location.href)
    const basePath = currentUrl.pathname.replace(/\/\d{4}-\d{2}-\d{2}$/, "")
    const [year, month, day] = currentNight.split("-")
    const dateForWaitlist = `${month}/${day}/${year}`

    return {
      ...initialPaths,
      assignments: `${basePath.replace(
        /\/reservations.*/,
        ""
      )}/assignments_v2?night=${currentNight}`,
      waitlist: `/manage/${marinaSlug}/waitlists/short_term?first_day=${dateForWaitlist}&last_day=${dateForWaitlist}`,
    }
  }, [currentNight, initialNight, initialPaths, marinaSlug])

  const handleDateChange = (newDate) => {
    const dateStr = format(newDate, "yyyy-MM-dd")
    setCurrentNight(dateStr)

    const newUrl = new URL(window.location.href)
    newUrl.pathname = newUrl.pathname.replace(
      /\/\d{4}-\d{2}-\d{2}$/,
      `/${dateStr}`
    )
    window.history.pushState({}, "", newUrl.toString())
  }

  const formatDateForPrint = (d) => {
    return d.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  const getPrintHeader = () => {
    if (filtersHook.arrival) {
      return `${counts?.arrivals ?? ""} Arrivals on ${formatDateForPrint(date)}`
    }
    if (filtersHook.departure) {
      return `${counts?.departures ?? ""} Departures on ${formatDateForPrint(
        date
      )}`
    }
    return `${counts?.tonight ?? ""} Boats on ${formatDateForPrint(date)}`
  }

  return (
    <div className="space-y-6 pb-12 print:space-y-2 print:pb-0">
      {/* Print Header - only visible in print mode */}
      <div className="hidden print:block">
        <h2 className="mb-2 mt-0 text-center text-xl font-semibold">
          {getPrintHeader()}
        </h2>
      </div>

      {/* Header - hidden in print */}
      <div className="print:hidden">
        <NightViewHeader
          date={date}
          paths={paths}
          onDateChange={handleDateChange}
        />
      </div>

      {/* Notes Bar - hidden in print */}
      <div className="print:hidden">
        <NotesBar marinaSlug={marinaSlug} date={currentNight} />
      </div>

      {/* Availability Bar - hidden in print */}
      <div className="print:hidden">
        <AvailabilityBarContainer
          marinaSlug={marinaSlug}
          night={currentNight}
        />
      </div>

      {/* Reservations Table */}
      <ReservationNightTable
        {...reservationNightTableProps}
        night={currentNight}
        counts={counts}
        countsLoading={countsLoading}
        activeStatCard={filtersHook.activeStatCard}
        onActiveStatCardChange={filtersHook.setActiveStatCard}
        filters={{
          search: filtersHook.search,
          arrival: filtersHook.arrival,
          departure: filtersHook.departure,
          bookingType: filtersHook.bookingType,
          selectedSpaceGroups: filtersHook.selectedSpaceGroups,
          showUnassigned: filtersHook.showUnassigned,
          insurance: filtersHook.insurance,
        }}
        onFiltersChange={(newFilters) => {
          if (newFilters.search !== filtersHook.search) {
            filtersHook.setSearch(newFilters.search)
          }
          if (newFilters.bookingType !== filtersHook.bookingType) {
            filtersHook.setBookingType(newFilters.bookingType)
          }
          if (
            JSON.stringify(newFilters.selectedSpaceGroups) !==
            JSON.stringify(filtersHook.selectedSpaceGroups)
          ) {
            filtersHook.setSelectedSpaceGroups(newFilters.selectedSpaceGroups)
          }
          if (newFilters.showUnassigned !== filtersHook.showUnassigned) {
            filtersHook.setShowUnassigned(newFilters.showUnassigned)
          }
          if (
            JSON.stringify(newFilters.insurance) !==
            JSON.stringify(filtersHook.insurance)
          ) {
            filtersHook.setInsurance(newFilters.insurance)
          }
        }}
      />
    </div>
  )
}

NightViewPage.propTypes = {
  night: PropTypes.string.isRequired,
  counts: PropTypes.shape({
    arrivals: PropTypes.number.isRequired,
    departures: PropTypes.number.isRequired,
    tonight: PropTypes.number.isRequired,
  }).isRequired,
  marinaSlug: PropTypes.string.isRequired,
  paths: PropTypes.shape({
    assignments: PropTypes.string.isRequired,
    waitlist: PropTypes.string.isRequired,
    availability: PropTypes.string,
  }).isRequired,
  reservationNightTableProps: PropTypes.object.isRequired,
}

export default NightViewPage
